import * as React from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { ConfirmationDialog } from '@/components/shared/confirmation-dialog'
import { useTranslations } from '@/hooks/use-translations'
import { useAdAccountsQuery, useMarketingSubscribersQuery, useSendMarketingMessage } from '@/queries/meta'

/**
 * Paid marketing messages on Messenger (`marketing_messages_messenger` /
 * `paid_marketing_messages`): people who accepted the opt-in request in a
 * conversation, and a message to all of them charged to an ad account.
 */
export function MarketingMessagesTab() {
  const { t } = useTranslations()
  const subscribers = useMarketingSubscribersQuery()
  const adAccounts = useAdAccountsQuery()
  const send = useSendMarketingMessage()
  const [adAccountId, setAdAccountId] = React.useState('')
  const [text, setText] = React.useState('')
  const [budget, setBudget] = React.useState('30')
  const [errors, setErrors] = React.useState<{ account?: string; text?: string; budget?: string }>({})
  const [confirming, setConfirming] = React.useState(false)

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    const next = {
      account: adAccountId ? undefined : t('facebook_page.marketing.account_required'),
      text: text.trim() ? undefined : t('facebook_page.marketing.text_required'),
      budget: Number(budget) >= 1 ? undefined : t('facebook_page.marketing.budget_invalid'),
    }
    setErrors(next)
    if (!next.account && !next.text && !next.budget) setConfirming(true)
  }

  const confirm = () =>
    send.mutate(
      { adAccountId, text, budget: Number(budget) },
      {
        onSuccess: (result) => {
          setConfirming(false)
          if (result.errors.length) toast.error(`${t('facebook_page.marketing.send_error')}: ${result.errors[0]}`)
          else toast.success(t('facebook_page.marketing.sent', { sent: result.sent }))
        },
        onError: (err) => {
          setConfirming(false)
          toast.error(`${t('facebook_page.marketing.send_error')}: ${err.message}`)
        },
      }
    )

  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">{t('facebook_page.marketing.subscribers_title')}</h2>
          <p className="text-sm text-muted-foreground">{t('facebook_page.marketing.subscribers_description')}</p>
        </div>
        {subscribers.isLoading && <Skeleton className="h-16 w-full" />}
        {subscribers.error && (
          <p className="text-sm text-destructive">{t('facebook_page.load_error')}: {subscribers.error.message}</p>
        )}
        {subscribers.data?.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('facebook_page.marketing.no_subscribers')}</p>
        )}
        <ul className="space-y-2">
          {subscribers.data?.map((subscriber) => (
            <li key={subscriber.token} className="flex flex-wrap items-center gap-2 rounded-lg border p-4 text-sm">
              <span className="font-semibold">{subscriber.topic || t('facebook_page.marketing.subscriber')}</span>
              <span className="text-muted-foreground">{t('facebook_page.marketing.psid', { psid: subscriber.psid })}</span>
              {subscriber.status && <Badge variant="outline">{subscriber.status}</Badge>}
            </li>
          ))}
        </ul>
      </section>

      <form onSubmit={submit} className="max-w-2xl space-y-4" noValidate>
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">{t('facebook_page.marketing.send_title')}</h2>
          <p className="text-sm text-muted-foreground">{t('facebook_page.marketing.send_description')}</p>
        </div>

        <div className="space-y-1">
          <Label htmlFor="marketing-account">{t('facebook_page.marketing.ad_account')}</Label>
          <Select value={adAccountId} onValueChange={setAdAccountId}>
            <SelectTrigger id="marketing-account" aria-invalid={!!errors.account}>
              <SelectValue placeholder={t('facebook_page.marketing.ad_account_placeholder')} />
            </SelectTrigger>
            <SelectContent>
              {adAccounts.data?.map((account) => (
                <SelectItem key={account.account_id} value={account.account_id}>
                  {account.name} ({account.currency})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError id="marketing-account-error">{errors.account}</FieldError>
        </div>

        <div className="space-y-1">
          <Label htmlFor="marketing-text">{t('facebook_page.marketing.text')}</Label>
          <Textarea
            id="marketing-text"
            aria-label={t('facebook_page.marketing.text')}
            rows={3}
            maxLength={640}
            value={text}
            placeholder={t('facebook_page.marketing.text_placeholder')}
            aria-invalid={!!errors.text}
            aria-describedby={errors.text ? 'marketing-text-error' : undefined}
            onChange={(event) => setText(event.target.value)}
          />
          <FieldError id="marketing-text-error">{errors.text}</FieldError>
        </div>

        <div className="space-y-1">
          <Label htmlFor="marketing-budget">{t('facebook_page.marketing.budget')}</Label>
          <Input
            id="marketing-budget"
            aria-label={t('facebook_page.marketing.budget')}
            inputMode="numeric"
            value={budget}
            aria-invalid={!!errors.budget}
            aria-describedby={errors.budget ? 'marketing-budget-error' : undefined}
            onChange={(event) => setBudget(event.target.value)}
          />
          <p className="text-xs text-muted-foreground">{t('facebook_page.marketing.budget_help')}</p>
          <FieldError id="marketing-budget-error">{errors.budget}</FieldError>
        </div>

        <Button type="submit" disabled={send.isPending}>{t('facebook_page.marketing.send')}</Button>
      </form>

      <ConfirmationDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t('facebook_page.marketing.confirm_title')}
        message={t('facebook_page.marketing.confirm_message', { count: subscribers.data?.length ?? 0 })}
        onConfirm={confirm}
        onCancel={() => setConfirming(false)}
        loading={send.isPending}
      />
    </div>
  )
}
